/* Only Snacks bundle builder.
   Pick a tier (number of boxes), distribute them across flavours, add to cart.
   Adding past the active tier moves you up a tier; the last tier is open-ended
   up to its data-os-max. Add to cart enables once the count reaches the tier.

   Display price = tier unit price x boxes (parsed from the tier's "$xx/ea"
   line), with the compare-at struck through when a cheaper tier or the
   subscription discount applies. Money at checkout comes from Shopify's own
   discounts/selling plans, so the page and the cart cannot disagree.

   Markup contract (all data-os-*): [data-os-pdp] root; [data-os-tier] buttons
   with data-os-max / data-os-price / data-os-plan; [data-os-flavour][data-variant]
   rows with [data-os-add], [data-os-step="±1"], [data-os-count]; [data-os-subscribe]
   with data-os-discount; [data-os-atc] with [data-os-atc-label] and
   [data-os-atc-price] > [data-os-atc-compare] + [data-os-atc-total].
   [data-os-nutri] > [data-os-nutri-select] + [data-os-nutri-label="key"] panels.
   Gallery thumbs are handled by os-sections.js ([data-os-thumbs]).
   Needs window.OSCart (os-cart.js loads first) for money formatting and adding. */
(() => {
  const TABLET_MAX = 1024; // mirrors --breakpoint-tablet: the gallery only sticks above it
  const STICKY_GAP = 16; // px between the sticky gallery and the header / viewport bottom

  // Tier prices are parsed from "$xx/ea" copy as dollars; the shop's own money
  // format (OSCart.money, in cents) paints them back.
  const money = (dollars) => window.OSCart.money(Math.round(dollars * 100));
  const number = (text) => parseFloat(String(text || '').replace(/[^\d.]/g, '')) || 0;
  // Shopify answers with this wording when the selling plan is not assigned to the variant.
  // Shopify's cart errors, mapped to a button label the shopper can act on:
  // a subscription-only product refuses one-time lines, a plan can be missing
  // from the variant, or the variant is out of stock.
  const failureLabel = (err) => {
    const message = err?.message || '';
    if (/only be purchased with a selling plan/i.test(message)) return 'One-time purchase not available yet';
    if (/selling plan/i.test(message)) return 'Subscription not available yet';
    if (/sold out|out of stock/i.test(message)) return 'Sold out right now';
    return 'Something went wrong';
  };

  const readTier = (el) => ({
    el,
    boxes: parseInt(el.dataset.osTier, 10) || 1,
    max: parseInt(el.dataset.osMax, 10) || parseInt(el.dataset.osTier, 10) || 1,
    unit: number(el.dataset.osPrice),
    plan: el.dataset.osPlan || '',
  });

  class OSBundle {
    constructor(root) {
      this.root = root;
      // The gift offer shares gallery/scroll behavior, while LP008 owns checkout.
      if (root.querySelector('os-gift-buybox')) {
        this.initStickyCta();
        return;
      }
      this.tiers = [...root.querySelectorAll('[data-os-tier]')].map(readTier);
      this.rows = [...root.querySelectorAll('[data-os-flavour]')];
      this.atc = root.querySelector('[data-os-atc]');
      this.atcLabel = root.querySelector('[data-os-atc-label]');
      this.priceEl = root.querySelector('[data-os-atc-price]');
      this.subscribe = root.querySelector('[data-os-subscribe]');
      this.freq = root.querySelector('[data-os-freq]');
      this.status = root.querySelector('[data-os-builder-status]');
      this.discount = this.subscribe ? (parseFloat(this.subscribe.dataset.osDiscount) || 0) : 0;
      this.baseLabel = this.atcLabel ? this.atcLabel.textContent.trim() : 'Add to cart';
      this.unit = (this.atc && this.atc.dataset.osUnit) || 'box'; // "box" for snack packs, "pack" for 4oz
      this.tier = this.tiers[0] || { boxes: 1, max: 1, unit: 0, plan: '' };
      this.picked = false; // a tier the shopper chose explicitly (lights the ladder even at 0 boxes)
      this.counts = new Map(this.rows.map((row) => [row, 0]));
      this.bind();
      this.initStickyCta();
      this.render();
    }

    initStickyCta() {
      if (!this.root.hasAttribute('data-os-funnel')) return;
      const cta = this.root.querySelector('[data-os-sticky-cta]');
      const form = this.root.querySelector('.os-pdp__form');
      if (!cta || !form) return;
      const controller = new AbortController();
      const update = () => {
        const heroCta = Array.from(document.querySelectorAll('a[data-os-sticky-trigger]'))
          .find((link) => link.hash === `#${this.root.id}`);
        const bounds = this.root.getBoundingClientRect();
        const builderVisible = bounds.top < window.innerHeight && bounds.bottom > 0;
        cta.hidden = builderVisible || (heroCta || form).getBoundingClientRect().bottom > 0;
      };
      let queued = false;
      window.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; update(); });
      }, { passive: true, signal: controller.signal });
      window.addEventListener('resize', update, { signal: controller.signal });
      const scrollToBuilder = () => {
        const header = document.querySelector('.os-header-wrap');
        const offset = header ? header.getBoundingClientRect().height : 0;
        window.scrollTo({ top: this.root.getBoundingClientRect().top + window.scrollY - offset,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      cta.addEventListener('click', scrollToBuilder, { signal: controller.signal });
      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey ||
            event.shiftKey || event.altKey || link.target || link.hasAttribute('download') ||
            !this.root.id || link.hash !== `#${this.root.id}` ||
            link.origin !== location.origin || link.pathname !== location.pathname ||
            link.search !== location.search) return;
        event.preventDefault();
        if (location.hash !== link.hash) history.pushState(null, '', link.hash);
        scrollToBuilder();
      }, { signal: controller.signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this.root)) controller.abort();
      }, { signal: controller.signal });
      update();
    }

    bind() {
      this.tiers.forEach((tier, index) => {
        tier.el.addEventListener('click', () => this.setTier(tier));
        tier.el.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tiers.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.tiers.length) % this.tiers.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tiers.length - 1;
          else return;
          event.preventDefault();
          this.setTier(this.tiers[next]);
          this.tiers[next].el.focus();
        });
      });
      this.rows.forEach((row) => {
        row.querySelector('[data-os-add]')?.addEventListener('click', () => this.bump(row, 1));
        row.querySelectorAll('[data-os-step]').forEach((btn) =>
          btn.addEventListener('click', () => this.bump(row, Number(btn.dataset.osStep))));
      });
      this.subscribe?.addEventListener('change', () => {
        if (this.freq) this.freq.hidden = !this.subscribed;
        this.render();
      });
      this.atc?.addEventListener('click', () => this.addToCart());
    }

    get total() { return [...this.counts.values()].reduce((a, b) => a + b, 0); }
    get maxBoxes() { return this.tiers.length ? this.tiers[this.tiers.length - 1].max : Infinity; }
    get subscribed() { return !!(this.subscribe && this.subscribe.checked); }

    // The tier a box count belongs to: the highest tier whose entry point is at
    // or below the count (0 boxes keeps the first tier).
    tierFor(total) {
      let match = this.tiers[0];
      this.tiers.forEach((tier) => { if (total >= tier.boxes) match = tier; });
      return match;
    }

    setTier(tier) {
      this.tier = tier;
      this.picked = true;
      this.trimTo(tier.max); // clicking a lower tier trims the selection to fit it
      this.render();
      this.announceSelection();
    }

    trimTo(max) {
      while (this.total > max) {
        const over = this.rows.find((row) => this.counts.get(row) > 0);
        if (!over) return;
        this.counts.set(over, this.counts.get(over) - 1);
      }
    }

    bump(row, delta) {
      if (delta > 0 && (row.dataset.available === 'false' || this.total >= this.maxBoxes)) return;
      this.counts.set(row, Math.max(0, this.counts.get(row) + delta));
      const total = this.total;
      // Adding past the active tier moves up; removing below it moves down.
      if (total > this.tier.max || (total > 0 && total < this.tier.boxes)) this.tier = this.tierFor(total);
      this.render();
      this.announceSelection();
    }

    announce(text) {
      if (!this.status) return;
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => { this.status.textContent = text; }, 300);
    }

    announceSelection() {
      const noun = this.total === 1 ? this.unit : this.unit === 'box' ? 'boxes' : `${this.unit}s`;
      this.announce(`${this.total} ${noun} selected. ${this.atcLabel?.textContent.trim() || ''}`);
    }

    // ---- rendering: one method per region of the buy box ----
    render() {
      const done = this.total > 0 && this.total >= this.tier.boxes;
      this.renderTiers();
      this.renderRows();
      this.renderPrice(done);
      this.renderAtc(done);
    }

    renderTiers() {
      // Nothing is lit with an empty box unless the shopper picked a tier themselves.
      const lit = this.total > 0 || this.picked;
      const activeIndex = lit ? this.tiers.indexOf(this.tier) : -1;
      this.tiers.forEach((tier, i) => {
        const on = lit && tier === this.tier;
        tier.el.classList.toggle('is-active', on);
        tier.el.classList.toggle('is-filled', i <= activeIndex); // rail + dots lit up to the active tier
        tier.el.setAttribute('aria-checked', on ? 'true' : 'false');
        tier.el.tabIndex = i === Math.max(0, activeIndex) ? 0 : -1;
      });
    }

    renderRows() {
      const full = this.total >= this.maxBoxes;
      this.rows.forEach((row) => {
        const count = this.counts.get(row);
        row.classList.toggle('is-selected', count > 0);
        row.querySelector('[data-os-count]').textContent = count;
        const add = row.querySelector('[data-os-add]');
        if (add) add.disabled = full || row.dataset.available === 'false';
        row.querySelectorAll('[data-os-step]').forEach((btn) => {
          btn.disabled = Number(btn.dataset.osStep) > 0 ? full || row.dataset.available === 'false' : count === 0;
        });
      });
    }

    renderPrice(done) {
      if (!this.priceEl) return;
      // Without the cart script there is no formatter (and no drawer): the
      // price line stays hidden and the button falls back to the cart page.
      if (!window.OSCart) { this.priceEl.hidden = true; return; }
      const { tier, total } = this;
      const baseUnit = this.tiers[0] ? this.tiers[0].unit : tier.unit;
      const list = tier.unit * total;
      const pay = this.subscribed && this.discount > 0
        ? Math.round(Math.round(tier.unit * 100) * (100 - this.discount) / 100) * total / 100
        : list;
      const compare = Math.max(baseUnit * total, list);
      const showCompare = compare > pay + 0.005;
      this.priceEl.querySelector('[data-os-atc-total]').textContent = money(pay);
      const strike = this.priceEl.querySelector('[data-os-atc-compare]');
      strike.textContent = showCompare ? money(compare) : '';
      strike.hidden = !showCompare;
      this.priceEl.hidden = !(done && tier.unit > 0);
    }

    renderAtc(done) {
      if (!this.atc) return;
      this.atc.disabled = !done;
      const missing = this.tier.boxes - this.total;
      const noun = missing === 1 ? this.unit : (this.unit === 'box' ? 'boxes' : `${this.unit}s`);
      if (this.atcLabel) this.atcLabel.textContent = done ? this.baseLabel : `Add at least ${missing} ${noun}`;
    }

    // ---- add to cart ----
    lineItems() {
      // Subscribe attaches the tier's selling plan to every line, so Shopify
      // creates the subscription contract at checkout and Recharge picks it up.
      // A chosen delivery frequency carries its own selling plan; otherwise the tier's.
      // Recharge issues one plan group per product, so a row's own plan for the
      // chosen frequency (data-plan-<weeks>) wins over the section-wide IDs.
      const freqOption = this.freq ? this.freq.querySelector('[data-os-freq-option]:checked') : null;
      const weeks = freqOption?.dataset.osWeeks || '';
      const fallbackPlan = this.subscribed ? Number(freqOption?.value || this.tier.plan) || null : null;
      return this.rows
        .filter((row) => this.counts.get(row) > 0)
        .map((row) => {
          const line = { id: Number(row.dataset.variant), quantity: this.counts.get(row) };
          // getAttribute: dataset does not camel-case a digit suffix (data-plan-4 is not dataset.plan4).
          const ownPlan = weeks ? Number(row.getAttribute(`data-plan-${weeks}`)) || null : null;
          const plan = this.subscribed ? ownPlan || fallbackPlan : null;
          if (plan) line.selling_plan = plan;
          return line;
        });
    }

    setLabel(text) { if (this.atcLabel) this.atcLabel.textContent = text; }

    // Puts the button back to rest with the given label.
    settle(label) {
      this.setLabel(label);
      this.atc.disabled = false;
      this.atc.classList.remove('is-busy');
    }

    async addToCart() {
      const items = this.lineItems();
      if (!items.length || !this.atc) return;
      // Subscribe ticked but no row resolved a plan: say so instead of adding a one-time line.
      if (this.subscribed && !items.some((item) => item.selling_plan)) {
        this.setLabel('Subscription not available yet');
        this.announce('Subscription not available yet');
        setTimeout(() => this.settle(this.baseLabel), 2500);
        return;
      }
      if (!window.OSCart) { window.location.href = `${window.Shopify?.routes?.root || '/'}cart`; return; } // no drawer
      const previous = this.atcLabel ? this.atcLabel.textContent : this.baseLabel;
      this.atc.disabled = true;
      this.atc.classList.add('is-busy');
      this.setLabel('Adding…');
      try {
        await window.OSCart.addItems(items); // adds, repaints the drawer and the badge in one request
        window.OSCart.open(this.atc);
        this.settle(previous);
      } catch (err) {
        console.error('[os-pdp] add to cart failed', err);
        this.setLabel(failureLabel(err));
        this.announce(failureLabel(err));
        setTimeout(() => this.settle(previous), 2500);
      }
    }
  }

  // Nutrition panel: the flavour picker swaps the label shown. One delegated
  // listener covers every panel, including ones the theme editor re-renders.
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-os-nutri-select]');
    if (!sel) return;
    sel.closest('[data-os-nutri]').querySelectorAll('[data-os-nutri-label]').forEach((el) => {
      el.hidden = el.dataset.osNutriLabel !== sel.value;
    });
  });

  // Desktop gallery column: sticky under the header when it fits the viewport;
  // when it is taller, pin its bottom edge instead so nothing empty shows below it.
  const placeGallery = (media) => {
    if (window.innerWidth <= TABLET_MAX) { media.style.removeProperty('--os-sticky-top'); return; }
    const header = document.querySelector('.os-header-wrap');
    const top = (header ? header.getBoundingClientRect().height : 0) + STICKY_GAP;
    const spare = window.innerHeight - media.offsetHeight - STICKY_GAP;
    media.style.setProperty('--os-sticky-top', `${Math.min(top, spare)}px`);
  };
  const galleries = () => document.querySelectorAll('.os-pdp__media');
  window.addEventListener('resize', () => galleries().forEach(placeGallery));
  const sizeWatch = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => entries.forEach((entry) => placeGallery(entry.target)))
    : null;
  const initStickyGallery = (root) => root.querySelectorAll('.os-pdp__media:not([data-os-sticky-ready])')
    .forEach((media) => {
      media.dataset.osStickyReady = 'true';
      placeGallery(media);
      sizeWatch?.observe(media);
    });

  const init = (root = document) => {
    root.querySelectorAll('[data-os-pdp]:not([data-os-pdp-ready])').forEach((el) => {
      el.dataset.osPdpReady = 'true';
      new OSBundle(el);
    });
    initStickyGallery(root);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
